import '@/polyfill'
import '../styles/index.scss'
import 'core-js/features/object/get-own-property-descriptors'
import { initializePlaylistPlayerBindings } from '@/playback/playerPlaylistBinding'
import { initializeMediaSessionBindings } from '@/playback/mediaSessionBinding'
import { bindBrowserLocale, setI18nConfig } from '@amzn/bf-i18n'
import { cblAuthConfig, stratusConfig } from '@amzn/vinyl-amzn/cbl'
import { getApiHost, useLiveHost } from '@/service/useLiveHost'
import { initializeAppView } from '@/AppView'
import { initializeConnectedObserver } from '@amazon/vinyl-tsx'
import { initializeNativePlatform } from '@/initializeNativePlatform'
import { hideMainLoader } from '@/hideMainLoader'
import { initializeDeviceInfo } from '@/device/device'
import { initializeOfflineCache } from '@/offline/initializeOfflineCache'
import { initializeSpatialKeyBindings } from '@/tv/initializeSpatialKeyBindings'
import { scrollIntoViewOnFocus } from '@/util/domUtil'
import { initializeDeviceStyling } from '@/device/initializeDeviceStyling'
import {
    createDisposer,
    getUserAgentInfo,
    logDebug,
    logInfo,
    noop,
    sleep,
} from '@amazon/vinyl-util'
import { ctx } from '@/util/context'
import { getDeviceConfig } from '@/device/deviceConfig'
import { blockIdleQueue } from '@/util/idleQueue'
import { initializeErrorHandling } from '@/errorHandler'
import { initializeRouter, registerRoutes } from '@/routes'
import { createAppDependencies } from '@/appDependencies'
import { initializeLogging } from '@/core/initializeLogging'
import { bullfrogVersion } from '@/version'
import { reloadApplication, setReloadApplicationHandler } from '@/core/reloadApplication'
import { getCastingBridge } from '@/casting/CastingBridge'
import { onConnected } from '@/bridge/CommandBridge'
import { initializeMediaKeyBindings } from '@/playback/mediaKeyBinding'
import { installOtaUpdater } from '@/ota/installOtaUpdater'
import { initializeTauri } from '@/service/initializeTauri'
import { installPlaySearchBridge } from '@/voice/playSearch'
import { checkAndShowCookieConsentModal } from '@/messaging/cookieConsentService'
import { initializeInactivityMonitor } from '@/messaging/inactivityMonitor'

// Service configuration:
stratusConfig.value = {
    host: getApiHost(),
    region: 'NA',
}
cblAuthConfig.value = {
    host: useLiveHost() ? 'https://api.amazon.com' : '/proxy/api',
}

const { target } = ctx('app')

void (async () => {
    await initializeTauri()
    initializeErrorHandling()
    initializeLogging()
    // Translation files live under html/i18n/<bundle>/<locale>.json. Match
    // the layout of the puff sources so each `getString` call reads from the
    // bundle that sourced it.
    setI18nConfig({ translationPath: '/i18n/{bundle}/{locale}.json' })
    bindBrowserLocale()
    await initializeDeviceInfo()
    initializeNativePlatform()
    initializeDeviceStyling()
    logInfo(target, `👋 Bullfrog ${bullfrogVersion.str}`)
    logDebug(target, 'initializing', getUserAgentInfo(), getDeviceConfig())

    // Don't start idle tasks until 5s after startup
    blockIdleQueue(sleep(5)).catch(noop)

    initializeOfflineCache()
    initializeConnectedObserver()
    hideMainLoader()
    scrollIntoViewOnFocus()
    installOtaUpdater()

    setReloadApplicationHandler(() => {
        const { add, dispose } = createDisposer()
        logDebug(target, 'reload application')
        const deps = add(createAppDependencies()).dependencies

        add(initializeAppView({ deps }))

        add(initializeRouter())
        add(registerRoutes(deps))
        // Voice bridge: lets the iOS Siri shell call window.bullfrog.playSearch()
        // and answers /play?q=... deep links. Hooked here because we want the
        // controller available before the WebView claims to be ready.
        installPlaySearchBridge({ cloudQueueController: deps.cloudQueueController })
        add(initializePlaylistPlayerBindings(deps))
        add(initializeMediaSessionBindings(deps))
        add(initializeMediaKeyBindings(deps))
        add(initializeSpatialKeyBindings())
        add(initializeInactivityMonitor(deps))
        add(checkAndShowCookieConsentModal())

        return () => {
            logDebug(target, 'dispose')
            dispose()
        }
    })

    // Reload the application when the casting connection connects and disconnects.
    onConnected(getCastingBridge().connectionStatus, () => {
        logDebug(target, 'casting connected')
        reloadApplication()
        return reloadApplication
    })
    reloadApplication()
})()
